/**
 * staleTabSelfHeal — the ONE owner of recovery-reload policy (returning-tab
 * reliability, 2026-09-01).
 *
 * THE BANNER-CONTRACT CARVE-OUT, stated plainly: the deploy-freshness
 * doctrine (2026-08-20, AppUpdateBanner.tsx) promises a mere update NEVER
 * auto-reloads — that stays click-only. This module reloads ONLY when the
 * running code is provably at risk of dead-ending:
 *   - 'chunk-failure'   a lazy chunk already failed through every retry
 *   - 'pruned-assets'   the running main bundle no longer serves as JS
 *                       (the deploy prune reached this boot's hashed files)
 *   - 'stale-boot-age'  this boot is older than the retention-coupled age
 * A live manifest that merely DIFFERS from the running build is never a
 * reload — that is exactly the banner's click.
 *
 * Every reload here: persists restore state first (the synchronous
 * 'prest0:before-self-heal-reload' flush event; route/hash survive natively
 * and the active thread, list, open/dock/fullscreen and composer draft
 * already restore from their persisted keys — the same guarantee the update
 * banner documents), respects the cross-tab streaming lease with the same
 * 10-minute fail-open the banner uses, and rides timestamped loop guards
 * that fail CLOSED (unusable storage ⇒ no reload — the
 * attemptFirestoreQuotaSelfHeal idiom in errorHandlers.ts).
 *
 * Self-heal is SILENT (the stealth idiom): no user-facing copy, ever. The
 * diagnostics stamp SELF_HEAL_LAST_KEY {at, reason, from, to} carries the
 * story for debugging.
 */

import { fetchLiveMainJs } from '../components/common/AppUpdateBanner';
import { isAnyTabStreaming } from '../components/GlobalChatSystem/streamingFlag';

export type SelfHealReason = 'chunk-failure' | 'pruned-assets' | 'stale-boot-age';

/** localStorage, epoch ms — cross-tab loop guard (timestamped, never latching). */
export const SELF_HEAL_RELOAD_AT_KEY = 'prest0_self_heal_reload_at';
/** sessionStorage 'count:ts' — per-tab reload cap over a rolling window. */
export const SELF_HEAL_TAB_RELOADS_KEY = 'prest0_self_heal_tab_reloads';
/** localStorage JSON {at, reason, from, to} — diagnostics only, never read by code. */
export const SELF_HEAL_LAST_KEY = 'prest0_self_heal_last';

/** Short on purpose: a sibling stale tab must not sit broken for long. */
export const SELF_HEAL_MIN_INTERVAL_MS = 90_000;
export const TAB_MAX_RELOADS = 2;
export const TAB_RELOAD_WINDOW_MS = 30 * 60_000;
/** Never race hydration / the sign-in migration window. */
export const BOOT_GRACE_MS = 30_000;
/** Wake probes cost a manifest fetch + maybe a bundle fetch — at most hourly per tab. */
export const PROBE_MIN_INTERVAL_MS = 60 * 60_000;
/**
 * The age belt. MUST stay well inside deploy.yml's asset retention (60 days
 * as of 2026-09-01 — the manifest-aware prune) or the belt fires only after
 * breakage is already possible. Bump retention first, this second.
 */
export const STALE_BOOT_AGE_MS = 30 * 24 * 3_600_000;
export const STREAM_WAIT_RECHECK_MS = 5_000;
/** Mirrors AppUpdateBanner's READY_FAIL_OPEN_MS: past a continuously-fresh
 * 10-minute lease streak the flag is overwhelmingly stuck, not real. */
export const STREAM_FAIL_OPEN_MS = 600_000;

/** Synchronous flush point dispatched strictly BEFORE location.reload() so
 * restore-marker writers (scroll anchors etc.) can persist. */
export const SELF_HEAL_BEFORE_RELOAD_EVENT = 'prest0:before-self-heal-reload';

/** Page epoch for boot-age math. timeOrigin everywhere modern; the legacy
 * navigationStart / module-eval fallbacks only ever make the boot look
 * YOUNGER, which fails toward not reloading. */
const REAL_BOOT_EPOCH_MS: number = (() => {
  try {
    if (typeof performance !== 'undefined') {
      if (Number.isFinite(performance.timeOrigin)) return performance.timeOrigin;
      const nav = (performance as unknown as { timing?: { navigationStart?: number } }).timing
        ?.navigationStart;
      if (typeof nav === 'number' && nav > 0) return nav;
    }
  } catch {
    /* fall through */
  }
  return Date.now();
})();

/** Mutable only through the test reset — jest's fake timers stub
 * performance.timeOrigin into a NOOP function, so tests inject an epoch. */
let bootEpochMs = REAL_BOOT_EPOCH_MS;

/** The CRA-injected main bundle's src, or null (dev server / tests) — null disarms everything. */
function runningMainSrc(): string | null {
  try {
    const el = document.querySelector('script[src*="/static/js/main."]');
    return el?.getAttribute('src') ?? null;
  } catch {
    return null;
  }
}

/** 'main.<hash>.js' from a src/manifest path, for build-identity compares. */
function mainBasename(path: string): string | null {
  const m = /main\.[A-Za-z0-9]+\.js/.exec(path);
  return m ? m[0] : null;
}

/** The last live main.js the manifest showed us — diagnostics ('to') only. */
let lastObservedLiveMain: string | null = null;
/** A committed (possibly lease-deferred) reload; later callers join it. */
let pendingReload: Promise<boolean> | null = null;

/**
 * Both loop guards, read-side. Fail CLOSED: a guard we cannot read is a
 * loop we cannot bound, so the answer is no reload.
 */
function guardsAllowReload(): boolean {
  try {
    const last = Number(localStorage.getItem(SELF_HEAL_RELOAD_AT_KEY) || 0);
    if (Number.isFinite(last) && last > 0 && Date.now() - last < SELF_HEAL_MIN_INTERVAL_MS) {
      return false;
    }
  } catch {
    return false;
  }
  try {
    const raw = sessionStorage.getItem(SELF_HEAL_TAB_RELOADS_KEY);
    if (raw) {
      const idx = raw.indexOf(':');
      const count = parseInt(raw.slice(0, idx), 10);
      const windowStart = parseInt(raw.slice(idx + 1), 10);
      const windowFresh =
        Number.isFinite(windowStart) && Date.now() - windowStart < TAB_RELOAD_WINDOW_MS;
      if (windowFresh && Number.isFinite(count) && count >= TAB_MAX_RELOADS) return false;
    }
  } catch {
    return false;
  }
  return true;
}

/**
 * Write-side of the guards + the diagnostics stamp. Returns false — and the
 * caller must NOT reload — when the guard stamps cannot be written (same
 * fail-closed rule as the read side). Diagnostics are best-effort.
 */
function writeGuardStamps(reason: SelfHealReason): boolean {
  const now = Date.now();
  try {
    localStorage.setItem(SELF_HEAL_RELOAD_AT_KEY, String(now));
    let count = 1;
    let windowStart = now;
    const raw = sessionStorage.getItem(SELF_HEAL_TAB_RELOADS_KEY);
    if (raw) {
      const idx = raw.indexOf(':');
      const prevCount = parseInt(raw.slice(0, idx), 10);
      const prevStart = parseInt(raw.slice(idx + 1), 10);
      if (
        Number.isFinite(prevCount) &&
        Number.isFinite(prevStart) &&
        now - prevStart < TAB_RELOAD_WINDOW_MS
      ) {
        count = prevCount + 1;
        windowStart = prevStart;
      }
    }
    sessionStorage.setItem(SELF_HEAL_TAB_RELOADS_KEY, `${count}:${windowStart}`);
  } catch {
    return false;
  }
  try {
    localStorage.setItem(
      SELF_HEAL_LAST_KEY,
      JSON.stringify({ at: now, reason, from: runningMainSrc(), to: lastObservedLiveMain }),
    );
  } catch {
    /* diagnostics only — never blocks the heal */
  }
  return true;
}

/**
 * Defer while an answer is streaming in any tab. Resolves the moment the
 * lease clears, or after a CONTINUOUSLY-fresh streak outlives
 * STREAM_FAIL_OPEN_MS (the banner's softenedSince pattern) — past that the
 * flag is far more likely stuck than real.
 */
function waitOutStreamingLease(): Promise<void> {
  if (!isAnyTabStreaming()) return Promise.resolve();
  const streakStart = Date.now();
  return new Promise((resolve) => {
    const tick = setInterval(() => {
      const now = Date.now();
      if (!isAnyTabStreaming(now) || now - streakStart >= STREAM_FAIL_OPEN_MS) {
        clearInterval(tick);
        resolve();
      }
    }, STREAM_WAIT_RECHECK_MS);
  });
}

async function commitReload(reason: SelfHealReason): Promise<boolean> {
  await waitOutStreamingLease();
  if (!writeGuardStamps(reason)) {
    pendingReload = null;
    return false;
  }
  try {
    window.dispatchEvent(new CustomEvent(SELF_HEAL_BEFORE_RELOAD_EVENT));
  } catch {
    /* a listener problem must not stop the heal */
  }
  window.location.reload();
  return true;
}

/**
 * The one decision point every recovery reload goes through. Resolves true
 * when a reload was committed (it may still be lease-deferred for up to the
 * fail-open window), false when policy refused.
 */
export async function attemptSelfHealReload(reason: SelfHealReason): Promise<boolean> {
  // A committed reload is already on its way — join it, don't stack another.
  if (pendingReload) return pendingReload;
  if (!guardsAllowReload()) return false;
  if (reason === 'chunk-failure') {
    // The chunk path arrives with no manifest evidence of its own. Offline
    // and the synthetic-500 mask (src/index.tsx's patched fetch resolves a
    // dead network as {status:500, application/json}) both read null here —
    // reloading then would trade a broken chunk for a broken page.
    const live = await fetchLiveMainJs();
    if (!live) return false;
    lastObservedLiveMain = live;
    if (pendingReload) return pendingReload; // the await yielded — re-check
  }
  pendingReload = commitReload(reason);
  return pendingReload;
}

/**
 * The wake watcher: on visibility→visible (throttled hourly, never inside
 * the boot grace) ask whether this tab's build is still the live one, and
 * if not, whether it is provably at risk. Mounted once from the App root as
 * a null-render effect; returns its teardown.
 */
export function initStaleTabSelfHeal(): () => void {
  // No CRA-injected bundle (dev server, jsdom) — nothing to compare, never arm.
  if (!runningMainSrc()) return () => {};
  let lastProbeAt = 0;

  const check = async (): Promise<void> => {
    const now = Date.now();
    if (now - bootEpochMs < BOOT_GRACE_MS) return;
    if (now - lastProbeAt < PROBE_MIN_INTERVAL_MS) return;
    lastProbeAt = now;

    const live = await fetchLiveMainJs();
    // Offline / synthetic-500 / SPA-fallback JSON all read null — evidence
    // is inconclusive, and inconclusive fails CLOSED.
    if (!live) return;
    lastObservedLiveMain = live;

    const running = runningMainSrc();
    const runningBase = running ? mainBasename(running) : null;
    const liveBase = mainBasename(live);
    if (!running || !runningBase || !liveBase) return;
    // Same build ⇒ done. A DIFFERENT live build alone is also never a
    // reload — a mere update stays the banner's click (2026-08-20 doctrine).
    if (liveBase === runningBase) return;

    // PROBE the running bundle itself (HEAD — the headers carry the verdict,
    // the body is megabytes we don't need). Only the two CONCLUSIVE prune
    // signatures may reload: 200-with-non-JS (the GCS lane serves the SPA
    // fallback as 200 text/html) and 404 (the hosting lane). Anything else —
    // a real 5xx, or a network failure the patched fetch in src/index.tsx
    // resolves as a synthetic {status:500, application/json} — is
    // inconclusive and fails CLOSED: a healthy tab must never reload on a
    // CDN hiccup or a Wi-Fi flap.
    let pruned = false;
    try {
      const resp = await fetch(running, {
        method: 'HEAD',
        cache: 'no-store',
        credentials: 'same-origin',
      });
      const contentType = (resp.headers.get('content-type') || '').toLowerCase();
      if (resp.status === 404) {
        pruned = true;
      } else if (resp.ok && !contentType.includes('javascript')) {
        pruned = true;
      } else if (!resp.ok) {
        return; // 5xx / masked network — inconclusive, fail closed
      }
    } catch {
      return; // the probe itself failed — inconclusive, fail closed
    }
    if (pruned) {
      void attemptSelfHealReload('pruned-assets');
      return;
    }
    // Belt: the bundle still serves but this boot is approaching the prune
    // window — heal on our schedule instead of breaking on the prune's.
    if (now - bootEpochMs > STALE_BOOT_AGE_MS) {
      void attemptSelfHealReload('stale-boot-age');
    }
  };

  const onVisibility = () => {
    if (document.visibilityState === 'visible') void check();
  };
  document.addEventListener('visibilitychange', onVisibility);
  // One pass shortly after boot too: a tab restored from the disk cache can
  // be stale without ever going hidden again after load.
  const bootTimer = window.setTimeout(() => void check(), BOOT_GRACE_MS);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.clearTimeout(bootTimer);
  };
}

/** Test-only: reset module state between cases; optionally inject the boot
 * epoch (fake timers stub performance.timeOrigin, so tests must supply it). */
export function __resetStaleTabSelfHealForTests(bootEpochOverride?: number): void {
  pendingReload = null;
  lastObservedLiveMain = null;
  bootEpochMs = bootEpochOverride ?? REAL_BOOT_EPOCH_MS;
}
